"use client";

import {
  CircleCheckIcon,
  InfoIcon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Toaster as Sonner, toast, type ToasterProps, useSonner } from "sonner";
import { useTheme } from "../hooks";
import { Spinner } from "./spinner";

// Reserve space at the toaster's vertical edges so the pinned "Dismiss all"
// pill fits between the stack and the viewport edge without overlap. Matches
// pill height (~28px) + edge inset + gap. Vertical-only — a single-number
// `offset` would also shift the horizontal inset and push toasts inward from
// the side edge.
const TOASTER_VERTICAL_OFFSET = { top: 56, bottom: 56 };

const Toaster = ({ ...props }: ToasterProps) => {
  const { themePreference } = useTheme();
  const position = props.position ?? "bottom-right";

  return (
    <>
      <Sonner
        theme={themePreference}
        className="toaster group"
        closeButton
        offset={TOASTER_VERTICAL_OFFSET}
        mobileOffset={TOASTER_VERTICAL_OFFSET}
        icons={{
          success: <CircleCheckIcon className="size-4" />,
          info: <InfoIcon className="size-4" />,
          warning: <TriangleAlertIcon className="size-4" />,
          error: <OctagonXIcon className="size-4" />,
          loading: <Spinner />,
        }}
        style={
          {
            "--normal-bg": "var(--surface-base)",
            "--normal-text": "var(--foreground)",
            "--normal-border": "var(--border)",
            "--border-radius": "var(--radius)",
          } as React.CSSProperties
        }
        toastOptions={{
          duration: 5000,
          classNames: {
            actionButton:
              "border! border-border-subtle! bg-surface-elevated! hover:bg-surface-hover! text-foreground!",
            description: "text-muted-foreground!",
            icon: "text-text-secondary!",
          },
        }}
        {...props}
      />
      <DismissAllButton position={position} zIndex={props.style?.zIndex} />
    </>
  );
};

function DismissAllButton({
  position,
  zIndex,
}: {
  position: NonNullable<ToasterProps["position"]>;
  zIndex?: React.CSSProperties["zIndex"];
}) {
  const { toasts } = useSonner();
  if (toasts.length < 2) return null;

  return (
    <button
      type="button"
      onClick={() => toast.dismiss()}
      style={{ ...cornerStyles(position), zIndex }}
      className="fixed rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs font-medium text-foreground shadow-raised cursor-pointer hover:bg-surface-hover transition-colors"
    >
      Dismiss all
    </button>
  );
}

function cornerStyles(
  position: NonNullable<ToasterProps["position"]>,
): React.CSSProperties {
  const edge = "1rem";
  const isTopAnchored = position.startsWith("top");
  const vertical: React.CSSProperties = isTopAnchored
    ? { top: edge }
    : { bottom: edge };

  if (position.endsWith("-left")) return { ...vertical, left: edge };
  if (position.endsWith("-right")) return { ...vertical, right: edge };
  return { ...vertical, left: "50%", transform: "translateX(-50%)" };
}

export { Toaster };
